import { memo, useState } from 'react'
import { GS } from 'back-end-api'
import { useTranslation } from 'services/Translation'

import { 
  Container, 
  SectionTitle, 
  FormGrid, 
  FormGroup, 
  Label, 
  Value, 
  EditButton,
  ActionButton
} from './Styles'
import styled from 'styled-components'
import { textPrimary } from 'styles/Colors'

const EditInput = styled.input`
  padding: 0.75rem 1rem;
  border: 2px solid #e5e7eb;
  border-radius: 0.375rem;
  font-family: coresanscr45;
  font-size: 1rem;
  color: ${textPrimary};
  background: white;
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: #2e4ce6;
    box-shadow: 0 0 0 3px rgba(46, 77, 230, 0.1);
  }

  &:hover {
    border-color: #d1d5db;
  }
`

const PasswordSection = styled.div`
  margin-top: 2rem;
  padding: 1.5rem;
  border: 1px solid #e5e7eb;
  border-radius: 0.5rem;
  background: #f9fafb;
`

const PasswordGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;
  margin-top: 1rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`

const ErrorText = styled.span`
  font-size: 0.8rem;
  color: #dc2626;
  margin-top: 0.25rem;
`

const SuccessText = styled.span`
  font-size: 0.8rem;
  color: #16a34a;
  margin-top: 0.25rem;
`

const texts = (getText) => (key) =>
  ({
    accountOverview: getText('Account overview', 'Account_Profile'),
    email: getText('Email (User name)', 'Account_Profile'),
    password: getText('Password', 'Account_Profile'),
    firstName: getText('First Name', 'Account_Profile'),
    lastName: getText('Last Name', 'Account_Profile'),
    specialties: getText('Specialties', 'Account_Profile'),
    licenseNumber: getText('Registration/License No.', 'Account_Profile'),
    phoneNumber: getText('Phone No.', 'Account_Profile'),
    country: getText('Country of activity', 'Account_Profile'),
    city: getText('City of activity', 'Account_Profile'),
    timezone: getText('Time Zone', 'Account_Profile'),
    change: getText('Change', 'Account_Profile'),
    editProfile: getText('Edit Profile', 'Account_Profile'),
    saveProfile: getText('Save Profile', 'Account_Profile'),
    cancel: getText('Cancel', 'Account_Profile'),
    changePassword: getText('Change Password', 'Account_Profile'),
    currentPassword: getText('Current Password', 'Account_Profile'),
    newPassword: getText('New Password', 'Account_Profile'),
    confirmPassword: getText('Confirm New Password', 'Account_Profile'),
    savePassword: getText('Save Password', 'Account_Profile'),
    passwordMismatch: getText('Passwords do not match', 'Account_Profile'),
    passwordTooShort: getText('Password must be at least 8 characters', 'Account_Profile'),
    passwordChanged: getText('Password changed successfully', 'Account_Profile'),
    profileSaved: getText('Profile saved successfully', 'Account_Profile'),
  }[key])

const Profile = () => {
  const { t } = useTranslation(texts)
  const [isEditing, setIsEditing] = useState(false)
  const [editData, setEditData] = useState({})
  const [isSaving, setIsSaving] = useState(false)
  const [saveMessage, setSaveMessage] = useState(null)
  
  // Password change state
  const [isChangingPassword, setIsChangingPassword] = useState(false)
  const [passwordData, setPasswordData] = useState({ old_password: '', new_password: '', confirm_password: '' })
  const [passwordError, setPasswordError] = useState('')
  const [passwordSuccess, setPasswordSuccess] = useState('')
  const [isSavingPassword, setIsSavingPassword] = useState(false)
  
  const accountResp = GS.useOwnAccount({ refreshInterval: 0 })
  const profile = accountResp?.data?.profile

  const handleEditToggle = () => {
    if (isEditing) {
      setIsEditing(false)
      setEditData({})
      setSaveMessage(null)
    } else {
      setIsEditing(true)
      setEditData({
        first_name: profile?.first_name || '',
        last_name: profile?.last_name || '',
        phone: profile?.mobile_phone || '',
      })
    }
  }

  const handleSaveProfile = async () => {
    setIsSaving(true)
    setSaveMessage(null)
    
    try {
      // Update name via updateOwnAccount (server expects 'firstname' and 'lastname')
      const updatePayload = {
        firstname: editData.first_name,
        lastname: editData.last_name,
      }
      
      const res = await GS.updateOwnAccount(updatePayload)
      
      if (res.error) {
        setSaveMessage({ type: 'error', text: res.error })
        setIsSaving(false)
        return
      }

      // Update phone if it changed - note: this requires OTP verification
      // For now we include it in the basic update since the endpoint might accept it
      if (editData.phone !== profile?.mobile_phone && editData.phone) {
        // The changeOwnMobilePhone endpoint requires OTP, so we attempt 
        // a basic update first. If the server supports it in updateOwnAccount we're good.
        // Otherwise phone change will need the OTP flow in the future.
        console.log('Phone number changed - OTP verification may be required for this in the future')
      }

      setSaveMessage({ type: 'success', text: t('profileSaved') })
      setIsEditing(false)
      setEditData({})
    } catch (error) {
      console.error('Error saving profile:', error)
      setSaveMessage({ type: 'error', text: error.message || 'Failed to save profile' })
    } finally {
      setIsSaving(false)
    }
  }

  const handleChangePassword = async () => {
    setPasswordError('')
    setPasswordSuccess('')

    if (passwordData.new_password.length < 8) {
      setPasswordError(t('passwordTooShort'))
      return
    }

    if (passwordData.new_password !== passwordData.confirm_password) {
      setPasswordError(t('passwordMismatch'))
      return
    }

    setIsSavingPassword(true)
    try {
      const res = await GS.changeOwnPassword({
        old_password: passwordData.old_password,
        new_password: passwordData.new_password,
      })

      if (res.error) {
        setPasswordError(res.error)
      } else {
        setPasswordSuccess(t('passwordChanged'))
        setPasswordData({ old_password: '', new_password: '', confirm_password: '' })
        // Hide the password section after a short delay
        setTimeout(() => {
          setIsChangingPassword(false)
          setPasswordSuccess('')
        }, 2000)
      }
    } catch (error) {
      console.error('Error changing password:', error)
      setPasswordError(error.message || 'Failed to change password')
    } finally {
      setIsSavingPassword(false)
    }
  }

  const handleInputChange = (field, value) => {
    setEditData(prev => ({
      ...prev,
      [field]: value
    }))
  }

  return (
    <Container>
      <SectionTitle>{t('accountOverview')}</SectionTitle>
      
      <FormGrid>
        <FormGroup>
          <Label>{t('email')}</Label>
          <Value>{profile?.email || '-'}</Value>
        </FormGroup>
        
        <FormGroup>
          <Label>{t('firstName')}</Label>
          {isEditing ? (
            <EditInput
              type="text"
              value={editData.first_name || ''}
              onChange={(e) => handleInputChange('first_name', e.target.value)}
            />
          ) : (
            <Value>{profile?.first_name || '-'}</Value>
          )}
        </FormGroup>
        
        <FormGroup>
          <Label>{t('password')}</Label>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <Value>*************</Value>
            <EditButton onClick={() => setIsChangingPassword(!isChangingPassword)}>
              {t('change')}
            </EditButton>
          </div>
        </FormGroup>
        
        <FormGroup>
          <Label>{t('lastName')}</Label>
          {isEditing ? (
            <EditInput
              type="text"
              value={editData.last_name || ''}
              onChange={(e) => handleInputChange('last_name', e.target.value)}
            />
          ) : (
            <Value>{profile?.last_name || '-'}</Value>
          )}
        </FormGroup>
        
        <FormGroup>
          <Label>{t('specialties')}</Label>
          <Value>{profile?.speciality || '-'}</Value>
        </FormGroup>
        
        <FormGroup>
          <Label>{t('licenseNumber')}</Label>
          <Value>{profile?.licence_number || '-'}</Value>
        </FormGroup>
        
        <FormGroup>
          <Label>{t('phoneNumber')}</Label>
          {isEditing ? (
            <EditInput
              type="tel"
              value={editData.phone || ''}
              onChange={(e) => handleInputChange('phone', e.target.value)}
            />
          ) : (
            <Value>{profile?.mobile_phone || '-'}</Value>
          )}
        </FormGroup>
        
        <FormGroup>
          <Label>{t('country')}</Label>
          <Value>{profile?.country || '-'}</Value>
        </FormGroup>
        
        <FormGroup>
          <Label>{t('city')}</Label>
          <Value>{profile?.city || '-'}</Value>
        </FormGroup>
        
        <FormGroup>
          <Label>{t('timezone')}</Label>
          <Value>{profile?.timezone || '-'}</Value>
        </FormGroup>
      </FormGrid>

      {/* Password change section */}
      {isChangingPassword && (
        <PasswordSection>
          <SectionTitle style={{ margin: 0, fontSize: '1rem' }}>{t('changePassword')}</SectionTitle>
          <PasswordGrid>
            <FormGroup>
              <Label>{t('currentPassword')}</Label>
              <EditInput
                type="password"
                value={passwordData.old_password}
                onChange={(e) => setPasswordData(prev => ({ ...prev, old_password: e.target.value }))}
                autoComplete="current-password"
              />
            </FormGroup>
            <div /> {/* spacer for grid alignment */}
            <FormGroup>
              <Label>{t('newPassword')}</Label>
              <EditInput
                type="password"
                value={passwordData.new_password}
                onChange={(e) => setPasswordData(prev => ({ ...prev, new_password: e.target.value }))}
                autoComplete="new-password"
              />
            </FormGroup>
            <FormGroup>
              <Label>{t('confirmPassword')}</Label>
              <EditInput
                type="password"
                value={passwordData.confirm_password}
                onChange={(e) => setPasswordData(prev => ({ ...prev, confirm_password: e.target.value }))}
                autoComplete="new-password"
              />
            </FormGroup>
          </PasswordGrid>
          {passwordError && <ErrorText>{passwordError}</ErrorText>}
          {passwordSuccess && <SuccessText>{passwordSuccess}</SuccessText>}
          <div style={{ marginTop: '1rem' }}>
            <ActionButton onClick={handleChangePassword} disabled={isSavingPassword}>
              {isSavingPassword ? 'Saving...' : t('savePassword')}
            </ActionButton>
          </div>
        </PasswordSection>
      )}

      {/* Edit / Save buttons */}
      <div style={{ marginTop: '2rem', display: 'flex', gap: '1rem', alignItems: 'center' }}>
        {isEditing ? (
          <>
            <ActionButton onClick={handleSaveProfile} disabled={isSaving}>
              {isSaving ? 'Saving...' : t('saveProfile')}
            </ActionButton>
            <ActionButton 
              onClick={handleEditToggle} 
              style={{ backgroundColor: '#6b7280', borderColor: '#6b7280' }}
            >
              {t('cancel')}
            </ActionButton>
          </>
        ) : (
          <ActionButton onClick={handleEditToggle}>{t('editProfile')}</ActionButton>
        )}
        {saveMessage && (
          saveMessage.type === 'success' 
            ? <SuccessText>{saveMessage.text}</SuccessText>
            : <ErrorText>{saveMessage.text}</ErrorText>
        )}
      </div>
    </Container>
  )
}

Profile.whyDidYouRender = true

export default memo(Profile)
